import { ChevronRight, Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { VISIBILITY_COPY } from './visibilityCopy';

interface PhotosetVisibilityBannerProps {
  onMakeVisible: () => void;
}

// `compact` is the mobile prototype's pill (24px, 12px type): the desktop
// size overflows a phone, where the internal console's icon rail leaves
// ~311px.
const HiddenPill = ({ compact = false }: { compact?: boolean }) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full bg-service-property-soft-foreground font-semibold text-background',
      compact
        ? 'h-6 px-2.5 text-xs leading-4'
        : 'h-[26px] px-3 text-[13px] leading-[17px]'
    )}
  >
    <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
    {VISIBILITY_COPY.pillHidden}
  </span>
);

/**
 * DV-2861: the strip above the page header while the photoset is hidden from
 * the client. Nothing renders while visible, at any width (product,
 * 2026-09-30, dropped the visible-state chip); Hide lives in the kebab.
 *
 * Two layouts, split in CSS like the header's own responsive pieces, so only
 * one is ever in the accessibility tree: lg+ is the desktop prototype's strip
 * (eyebrow · divider · pill · button); below lg the whole strip is one
 * full-width button (mobile prototype), which clears the 44px tap floor. It
 * may wrap: at 320px, beside the internal console's rail, one line does not
 * fit, and `ml-auto` keeps the action right-aligned on either line.
 */
const PhotosetVisibilityBanner = ({
  onMakeVisible
}: PhotosetVisibilityBannerProps) => (
  <div
    data-testid="photoset-visibility-banner"
    className="shrink-0 border-b border-primary bg-service-property-soft"
  >
    <div className="flex items-center gap-3 px-4 py-2 max-lg:hidden">
      <Typography
        variant="small-semibold"
        className="uppercase tracking-wide"
      >
        {VISIBILITY_COPY.bannerEyebrow}
      </Typography>
      <span aria-hidden="true" className="h-[18px] w-px bg-border" />
      <HiddenPill />
      <span className="flex-1" />
      <Button
        type="button"
        variant="primaryOutline"
        size="sm"
        onClick={onMakeVisible}
      >
        <Eye className="h-4 w-4" />
        {VISIBILITY_COPY.bannerAction}
      </Button>
    </div>
    {/* Explicit name: computed from content, the adjacent inline spans
        run together ("Internal OnlyHidden from ClientMake Visible"). */}
    <button
      type="button"
      onClick={onMakeVisible}
      aria-label={`${VISIBILITY_COPY.bannerEyebrow}: ${VISIBILITY_COPY.pillHidden}. ${VISIBILITY_COPY.bannerActionShort}`}
      className="flex min-h-11 w-full flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-left lg:hidden"
    >
      <Shield
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
      />
      <HiddenPill compact />
      <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[13px] leading-[17px] font-semibold text-primary">
        {VISIBILITY_COPY.bannerActionShort}
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </span>
    </button>
  </div>
);

export default PhotosetVisibilityBanner;
